import { Error } from '@/components/shared';
import { Api } from '@/infrastructure';
import { Order, PreferredDate, Recreation } from '@/types';
import { isEmpty } from '@/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import axios, { AxiosError } from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ModalFormValues, createModalFormSchema } from './modalFormSchema';

type Props = {
  order: Order;
  recreation: Recreation;
};

export const ModalForm: React.FC<Props> = (props) => {
  const { order, recreation } = props;
  const [preferredDate, setPreferredDate] = useState<PreferredDate>();
  const [errors, setErrors] = useState<Array<string>>([]);
  const [isCouponApplied, setIsCouponApplied] = useState<boolean>();

  const isOnline = recreation.kind.key === 'online';
  const schema = useMemo(() => createModalFormSchema(isOnline), [isOnline]);

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { isValid },
  } = useForm<ModalFormValues>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      year: '',
      month: '',
      day: '',
      startHour: '',
      startMinute: '',
      endHour: '',
      endMinute: '',
      numberOfPeople: order.numberOfPeople,
      numberOfFacilities: order.numberOfFacilities,
      zip: order.zip,
      prefecture: order.prefecture,
      city: order.city,
      street: order.street,
      building: order.building,
      hostingOfficeName: order.hostingOfficeName,
      couponCode: order.couponCode,
      transportationExpenses: isOnline ? 0 : undefined,
      expenses: order.expenses ?? 0,
    },
  });

  useEffect(() => {
    (async () => {
      try {
        setErrors([]);
        const response = await Api.get<PreferredDate>(
          'orders/preferred_date',
          'customer'
        );
        setPreferredDate(response.data);
      } catch (e) {
        if (axios.isAxiosError(e)) {
          const axiosError = e as AxiosError<Array<string>>;
          if (axiosError.response) {
            setErrors(axiosError.response.data);
            console.log(axiosError.response.data);
          }
        }
      }
    })();
  }, []);

  const handleApplyCouponCode = (input: string) => {
    if (input === 'えぶりぷらす') {
      setIsCouponApplied(true);
      setValue('couponCode', input);
      return;
    }
    setIsCouponApplied(false);
  };

  // === ここから希望日のUI制御 ===
  // マウント時点の“今日”を固定参照（レンダーごとに変えない）
  const today = useMemo(() => new Date(), []);
  const year = today.getFullYear();
  const month = today.getMonth() + 1; // 1..12
  const date = today.getDate();

  const daysInMonth = (y: number, m: number) => new Date(y, m, 0).getDate();

  // 現在の選択状態を監視
  const watchYear = watch('year');
  const watchMonth = watch('month');
  const watchNumberOfPeople = watch('numberOfPeople');
  const watchTransportationExpenses = watch('transportationExpenses');
  const watchExpenses = watch('expenses');

  // 合計金額（税別）：元の合計から旧値を引き、フォームの入力値を加算
  const totalPriceExcludingTax = useMemo(() => {
    const base = order?.totalPriceForCustomer ?? 0;
    const originalMaterialTotal =
      (order?.numberOfPeople ?? 0) * (order?.materialPrice ?? 0);
    const originalTransportation = order?.transportationExpenses ?? 0;
    const originalExpenses = order?.expenses ?? 0;
    const newMaterialTotal =
      Number(watchNumberOfPeople || 0) * (order?.materialPrice ?? 0);
    const newTransportation = Number(watchTransportationExpenses || 0);
    const newExpenses = Number(watchExpenses || 0);

    return (
      base -
      originalMaterialTotal -
      originalTransportation -
      originalExpenses +
      newMaterialTotal +
      newTransportation +
      newExpenses
    );
  }, [
    order?.totalPriceForCustomer,
    order?.numberOfPeople,
    order?.materialPrice,
    order?.transportationExpenses,
    order?.expenses,
    watchNumberOfPeople,
    watchTransportationExpenses,
    watchExpenses,
  ]);

  // 月の候補：今年が選択されている場合は今月以降、来年以降は全月
  const monthOptions = useMemo(() => {
    const all = preferredDate?.months ?? [];
    const y = parseInt(watchYear || '', 10);
    if (Number.isNaN(y)) return []; // 年未選択
    if (y === year) {
      return all.filter((m) => parseInt(m, 10) >= month);
    }
    return all;
  }, [preferredDate?.months, watchYear, year, month]);

  // 日の候補：今年かつ今月は“今日以降”、その他は1..末日
  const dayOptions = useMemo(() => {
    const y = parseInt(watchYear || '', 10);
    const m = parseInt(watchMonth || '', 10);
    if (Number.isNaN(y) || Number.isNaN(m)) return []; // 年/月未選択
    const last = daysInMonth(y, m);

    if (y === year && m === month) {
      return Array.from({ length: last - date + 1 }, (_, i) =>
        String(date + i)
      );
    }
    // preferredDate.days の中身が 1..31 固定でなくても、月末に合わせて生成
    return Array.from({ length: last }, (_, i) => String(i + 1));
  }, [watchYear, watchMonth, year, month, date]);

  // 年変更で月/日が無効になったら自動クリア
  useEffect(() => {
    const currentMonth = getValues('month');
    if (currentMonth && !monthOptions.includes(currentMonth)) {
      setValue('month', '');
      setValue('day', ''); // 月が変わると日も無効化の可能性があるのでクリア
    }
  }, [monthOptions, getValues, setValue]);

  // 月変更で日が無効になったら自動クリア
  useEffect(() => {
    const currentDay = getValues('day');
    if (currentDay && !dayOptions.includes(currentDay)) {
      setValue('day', '');
    }
  }, [dayOptions, getValues, setValue]);
  // === 希望日のUI制御ここまで ===

  const onSubmit = async (values: ModalFormValues): Promise<void> => {
    setErrors([]);
    const startAt: Date = new Date(
      `${getValues('year')}-${getValues('month')}-${getValues(
        'day'
      )} ${getValues('startHour')}:${getValues('startMinute')}`
    );

    const endAt: Date = new Date(
      `${getValues('year')}-${getValues('month')}-${getValues(
        'day'
      )} ${getValues('endHour')}:${getValues('endMinute')}`
    );

    const requestBody: {
      [key: string]: Omit<
        ModalFormValues,
        | 'year'
        | 'month'
        | 'day'
        | 'startHour'
        | 'startMinute'
        | 'endHour'
        | 'endMinute'
      > & { startAt: Date; endAt: Date };
    } = {
      order: {
        zip: values.zip,
        prefecture: values.prefecture,
        city: values.city,
        street: values.street,
        building: values.building,
        numberOfPeople: values.numberOfPeople,
        numberOfFacilities: values.numberOfFacilities,
        couponCode: isCouponApplied ? values.couponCode : '',
        hostingOfficeName: values.hostingOfficeName,
        transportationExpenses: values.transportationExpenses,
        expenses: values.expenses,
        startAt,
        endAt,
      },
    };

    try {
      await Api.patch<Order>(`orders/${order.id}`, 'customer', requestBody);
      window.location.href = `/customers/orders/${order.id}/complete`;
    } catch (e) {
      if (axios.isAxiosError(e)) {
        const axiosError = e as AxiosError<Array<string>>;
        if (axiosError.response) {
          setErrors(axiosError.response.data);
          console.log(axiosError.response.data);
        }
      }
      console.log(e);
    }
  };

  return (
    <div
      className='modal'
      id='orderModal'
      tabIndex={-1}
      aria-labelledby='orderModalLabel'
      aria-hidden='true'
    >
      <div className='modal-dialog modal-lg'>
        <div className='modal-content'>
          <div className='modal-header'>
            <h5 className='modal-title' id='orderModalLabel'>
              正式依頼フォーム
            </h5>
          </div>
          <div className='modal-body p-2'>
            {!isEmpty(errors) && <Error errors={errors} />}
            <form className='order h-adr' onSubmit={handleSubmit(onSubmit)}>
              <div className='container-fluid'>
                <div className='row pb-3'>
                  <div className='col-auto'>
                    <h3 className='title-b p-0'>
                      開催したい希望日と時間を選択してください
                      <span className='label required'>必須</span>
                    </h3>
                    <span className='text-muted'>
                      講師都合で希望日時の開催が難しい場合がございます。事前にチャットでの開催日時の相談をお願いします。
                    </span>
                  </div>
                </div>
                <div className='date-form'>
                  <div className='row'>
                    <div className='col-12'>希望日</div>
                    <div className='form-group col-3'>
                      <select {...register('year')} className='form-control'>
                        <option value=''></option>
                        {(preferredDate?.years ?? []).map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className='col-auto p-0 flex-v-c'>年</div>
                    <div className='form-group col-3'>
                      <select {...register('month')} className='form-control'>
                        <option value=''></option>
                        {monthOptions.map((month) => (
                          <option key={month} value={month}>
                            {month}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className='col-auto p-0 flex-v-c'>月</div>
                    <div className='form-group col-3'>
                      <select {...register('day')} className='form-control'>
                        <option value=''></option>
                        {dayOptions.map((day) => (
                          <option key={day} value={day}>
                            {day}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className='col-auto p-0 flex-v-c'>日</div>
                  </div>
                  <div className='row'>
                    <div className='col-12'>希望時間</div>
                    <div className='form-group col-2 pe-0'>
                      <select
                        {...register('startHour')}
                        className='form-control'
                      >
                        <option value=''></option>
                        {(preferredDate?.hours ?? []).map((hour) => (
                          <option key={hour} value={hour}>
                            {hour}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className='col-auto p-0 flex-v-c'>:</div>
                    <div className='form-group col-2 pe-0'>
                      <select
                        {...register('startMinute')}
                        className='form-control'
                      >
                        <option value=''></option>
                        {(preferredDate?.minutes ?? []).map((minute) => (
                          <option key={minute} value={minute}>
                            {minute}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className='col-auto p-0 flex-v-c'>~</div>
                    <div className='form-group col-2 pe-0'>
                      <select {...register('endHour')} className='form-control'>
                        <option value=''></option>
                        {(preferredDate?.hours ?? []).map((hour) => (
                          <option key={hour} value={hour}>
                            {hour}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className='col-auto p-0 flex-v-c'>:</div>
                    <div className='form-group col-2 pe-0'>
                      <select
                        {...register('endMinute')}
                        className='form-control'
                      >
                        <option value=''></option>
                        {(preferredDate?.minutes ?? []).map((minute) => (
                          <option key={minute} value={minute}>
                            {minute}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                <div className='row pt-3'>
                  <label className='col-12 title-b pb-3' htmlFor='participant'>
                    参加予定人数を入力してください
                    <span className='label required'>必須</span>
                  </label>
                  <div className='form-group col-3'>
                    <input
                      {...register('numberOfPeople')}
                      className='form-control text-right'
                    />
                  </div>
                  <div className='col-auto p-0 flex-v-c'>人</div>
                </div>

                {(recreation.kind.key === 'visit' ||
                  recreation.kind.key === 'mailing') && (
                  <>
                    <div className='row pt-3'>
                      <label
                        className='col-12 title-b pb-1'
                        htmlFor='transportationExpenses'
                      >
                        交通費を入力してください
                        <span className='label required'>必須</span>
                      </label>
                      <span className='col-12 text-muted font-12 pb-3'>
                        チャット内で講師と合意した金額を入力してください。
                        <br />
                        交通費が発生しない場合は０（ゼロ）を入力してください。
                      </span>
                      <div className='form-group col-3'>
                        <input
                          {...register('transportationExpenses')}
                          type='number'
                          className='form-control text-right'
                        />
                      </div>
                      <div className='col-auto p-0 flex-v-c'>円</div>
                    </div>
                    <div className='row pt-3'>
                      <label className='col-12 title-b pb-1' htmlFor='expenses'>
                        諸経費を入力してください
                      </label>
                      <span className='col-12 text-muted font-12 pb-3'>
                        諸経費（郵送費）が発生する場合のみ金額を変更してください。
                      </span>
                      <div className='form-group col-3'>
                        <input
                          {...register('expenses')}
                          type='number'
                          className='form-control text-right'
                        />
                      </div>
                      <div className='col-auto p-0 flex-v-c'>円</div>
                    </div>
                  </>
                )}

                {recreation.kind.key === 'online' && (
                  <div className='row pt-3'>
                    <label
                      className='col-12 title-b pb-3'
                      htmlFor='participant'
                    >
                      追加で参加する施設がある場合施設数をご記入ください
                    </label>
                    <div className='form-group col-3'>
                      <input
                        {...register('numberOfFacilities')}
                        className='form-control text-right'
                      />
                    </div>
                    <div className='col-auto p-0 flex-v-c'>施設</div>
                  </div>
                )}

                <div className='row pt-3'>
                  <label className='col-12 title-b pb-3' htmlFor='participant'>
                    <span>今回開催する施設の情報を入力してください</span>
                    <span className='label required'>必須</span>
                  </label>
                  <div className='form-group col-12'>
                    <input
                      {...register('hostingOfficeName')}
                      className='form-control text-right'
                    />
                  </div>
                  <div className='form-group col-12'>
                    <span className='text-muted'>
                      登録情報が反映されています。変更がある場合は編集してください。
                    </span>
                  </div>
                </div>

                <div className='row pt-3'>
                  <span className='p-country-name' style={{ display: 'none' }}>
                    Japan
                  </span>
                  <label className='col-12 title-b pb-3' htmlFor='participant'>
                    <span>今回の開催する施設の住所を入力してください</span>
                    <span className='label required'>必須</span>
                  </label>
                  <div className='form-group col-6'>
                    <label htmlFor='postalCode'>郵便番号</label>
                    <div className='input-group mb-3'>
                      <input
                        {...register('zip')}
                        className='form-control p-postal-control'
                        placeholder='郵便番号を入力'
                        autoComplete='off'
                      />
                      {/* <button
                        // id='searchAddressWithZipForOrder'
                        // onClick={() => handleZipChange()}
                        className='btn btn-outline-secondary p-postal-code-find'
                        type='button'
                      >
                        検索
                      </button> */}
                    </div>
                  </div>
                  <div className='form-group col-6'>
                    <label htmlFor='postalCode'>都道府県</label>
                    {/* <select
                      {...register('prefecture')}
                      className='form-control p-region'
                      onChange={(e) =>
                        handleCityChange(
                          filterCurrentPrefecture(e.target.value).prefCode
                        )
                      }
                      autoComplete='off'
                    >
                      {prefectures.map((prefecture) => (
                        <option
                          key={prefecture.prefName}
                          value={prefecture.prefName}
                        >
                          {prefecture.prefName}
                        </option>
                      ))}
                    </select> */}
                    <input
                      {...register('prefecture')}
                      className='form-control p-region'
                      autoComplete='off'
                    />
                  </div>
                  <div className='form-group col-6'>
                    <label htmlFor='postalCode'>市区町村</label>
                    {/* <select
                      {...register('city')}
                      className='form-control p-region'
                      autoComplete='off'
                    >
                      {cities.map((city) => (
                        <option key={city.cityName} value={city.cityName}>
                          {city.cityName}
                        </option>
                      ))}
                    </select> */}
                    <input
                      {...register('city')}
                      className='form-control p-region'
                      autoComplete='off'
                    />
                  </div>
                  <div className='form-group col-6'>
                    <label htmlFor='postalCode'>町名/番地</label>
                    <input
                      {...register('street')}
                      className='form-control p-postal-control'
                      autoComplete='off'
                    />
                  </div>
                  <div className='form-group col-12'>
                    <label htmlFor='postalCode'>建物名</label>
                    <input
                      {...register('building')}
                      className='form-control p-postal-control'
                      autoComplete='off'
                    />
                  </div>
                </div>

                <div className='input-group w-50'>
                  <label className='col-12 title-b py-3' htmlFor='participant'>
                    <span>クーポンコードをお持ちの方</span>
                  </label>
                  <input
                    {...register('couponCode')}
                    className='form-control text-right'
                  />
                  <button
                    onClick={() =>
                      handleApplyCouponCode(getValues('couponCode') ?? '')
                    }
                    className='btn btn-csecondar ms-2 px-3 rounded border-0 text-white font-weight-bold'
                    type='button'
                  >
                    適用
                  </button>
                </div>

                <div className='card mt-3'>
                  <div id='OrderForm'></div>
                  <div className='card-body'>
                    <h5 className='card-title title-b'>レク 費用</h5>
                    <div className='row justify-content-between border-bottom-dotted pb-2'>
                      <div className='col-auto'>開催費</div>
                      <div className='col-auto'>
                        &yen;
                        {order?.price?.toLocaleString()}
                      </div>
                    </div>
                    <div className='row justify-content-between border-bottom-dotted py-2'>
                      <div className='col-auto'>
                        <span id='participantNum'>材料費/1人</span>
                        <br />
                        <span className='text-muted font-12 color-ba08'>
                          ※材料費は人数分の費用が発生します
                        </span>
                      </div>
                      <div id='materialPrice' className='col-auto'>
                        &yen;
                        {order?.materialPrice?.toLocaleString()}
                      </div>
                    </div>
                    {(recreation.kind.key === 'visit' ||
                      recreation.kind.key === 'mailing') && (
                      <>
                        <div className='row justify-content-between border-bottom-dotted pb-2'>
                          <div className='col-auto'>交通費</div>
                          <div
                            id='transportationExpensesForOrderForm'
                            className='col-auto'
                          >
                            &yen;
                            {Number(
                              watchTransportationExpenses || 0
                            ).toLocaleString()}
                          </div>
                        </div>
                        <div className='row justify-content-between border-bottom-dotted pb-2'>
                          <div className='col-auto'>諸経費</div>
                          <div id='expensesForOrderForm' className='col-auto'>
                            &yen;
                            {Number(watchExpenses || 0).toLocaleString()}
                          </div>
                        </div>
                      </>
                    )}
                    {recreation.kind.key === 'online' && (
                      <div className='row justify-content-between border-bottom-dotted pb-2'>
                        <div className='col-auto'>
                          追加施設費 /
                          <span id='numberOfFacilitiesForOrderFormLabel'>
                            {order.numberOfFacilities} 施設
                          </span>
                        </div>
                        <div
                          id='numberOfFacilitiesForOrderForm'
                          className='col-auto'
                        >
                          {order?.totalFacilityPriceForCustomer?.toLocaleString()}
                        </div>
                      </div>
                    )}
                    <div className='row justify-content-between border-top py-3'>
                      <div className='col-auto'>合計(税別)</div>
                      <div id='totalPriceForOrderForm' className='col-auto'>
                        &yen;
                        {totalPriceExcludingTax.toLocaleString()}
                      </div>
                    </div>
                    {isCouponApplied && (
                      <div className='alert alert-warning' role='alert'>
                        <div className='font-weight-boild'>
                          クーポン適用中のため合計金額は「&yen; 0」となります
                        </div>
                        <div>
                          合計金額が表示されていても請求は&yen;
                          0となります。ご安心ください。
                        </div>
                      </div>
                    )}
                    {isCouponApplied === false && (
                      <div className='alert alert-danger' role='alert'>
                        このクーポンは存在していません
                      </div>
                    )}
                  </div>
                </div>

                <div className='card mt-4'>
                  <div className='card-body'>
                    <h5 className='card-title title-b'>注意事項</h5>
                    <p>
                      ・一度承認されたレクのキャンセルはできません。規約に準じた日程の変更は可能です。詳しくは利用規約をご確認ください。
                    </p>
                    <p>
                      ・材料費、オンラインレクの追加施設数、諸経費は開催までの間に講師とチャットで相談をして変更することが可能です。その場合、金額の変動がございます。
                    </p>
                    <p>
                      ・材料費が必要なレクは参加人数が増える場合や減る場合は講師とチャット相談をしてください。人数が減る場合、レクによっては準備した分の材料費分の請
                      求が発生する場合がございます。詳しくは講師にご確認をお願い致します。
                    </p>
                  </div>
                </div>
                <div className='row justify-content-center py-3'>
                  <div className='col-7 mb-3 text-center'>
                    <span className='text-muted'>
                      ※講師が承認をする事で正式に開催が決まります
                    </span>
                  </div>
                  <div className='col-7 text-center'>
                    <button
                      type='submit'
                      className={
                        isValid
                          ? 'btn btn-cprimary rounded-pill font-14 py-3 px-4 font-weight-bold text-white'
                          : `btn-cpr disabled`
                      }
                      disabled={!isValid}
                    >
                      講師にレク開催を正式依頼する
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
